// Lightweight global auth store for non-React modules (e.g., axios interceptors)
// Provides a single source of truth for the current JWT tokens with subscription support

export type TokenListener = (token: string | null) => void;

class AuthStore {
  private token: string | null = null;
  private refreshToken: string | null = null;
  private listeners: Set<TokenListener> = new Set();

  getToken(): string | null {
    if (this.token !== null) return this.token;
    // Fallback to localStorage for initial hydration
    return typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  }

  getRefreshToken(): string | null {
    if (this.refreshToken !== null) return this.refreshToken;
    // Fallback to localStorage for initial hydration
    return typeof window !== 'undefined' ? localStorage.getItem('refreshToken') : null;
  }

  setToken(newToken: string | null) {
    this.token = newToken;
    // Keep localStorage in sync as a persistence backup
    if (typeof window !== 'undefined') {
      if (newToken) localStorage.setItem('token', newToken);
      else localStorage.removeItem('token');
    }
    this.listeners.forEach((fn) => fn(newToken));
  }

  setRefreshToken(newRefreshToken: string | null) {
    this.refreshToken = newRefreshToken;
    // Keep localStorage in sync as a persistence backup
    if (typeof window !== 'undefined') {
      if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);
      else localStorage.removeItem('refreshToken');
    }
  }

  setTokens(accessToken: string | null, refreshToken: string | null) {
    this.setToken(accessToken);
    this.setRefreshToken(refreshToken);
  }

  clearTokens() {
    this.setToken(null);
    this.setRefreshToken(null);
  }

  subscribe(listener: TokenListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

const authStore = new AuthStore();
export default authStore;

